import { inject } from '@angular/core';
import { CanActivateFn } from '@angular/router';
import { OnlinePlayOnlineGameService } from '@core/online-play/services/online-play-online-game.service';
import { onlineGameplayTypeAlwaysShowEndScreen } from '@core/online-play/utils/online-gameplay.utils';
import { AlertService } from '@dc-core/dc-services/alert.service';
import { AuthService } from '@services/auth.service';
import { tap } from 'rxjs';
import { AfterGameService } from 'src/app/modules/games/services/after-game.service';

import { GamesLeaveBusterService } from '../services/games-leave-buster.service';

export const gamesOnlineLeaveBusterGuard: CanActivateFn = () => {
    const leaveBusterService: GamesLeaveBusterService = inject(GamesLeaveBusterService);
    const alertService: AlertService = inject(AlertService);
    const afterGameService: AfterGameService = inject(AfterGameService);
    const onlinePlayOnlineGameService: OnlinePlayOnlineGameService = inject(OnlinePlayOnlineGameService);
    const authService: AuthService = inject(AuthService);

    if (leaveBusterService.isAllowedToLeaveOnlineGame()) {
        if (leaveBusterService.gameIsFinished) {
            leaveBusterService.currentGameplayType.set(null);
        } else {
            afterGameService.cleanupAfterOnlineGame(true);
        }

        return true;
    } else {
        if (onlinePlayOnlineGameService.currentOnlineGame() === null) {
            alertService.createAlert({
                title: $localize`:@@MATCH_START_SUSPENDED:Matchmaking cancelled`,
                icon: 'warning',
                showCloseButton: true,
            });

            return true;
        }
        if (leaveBusterService.isOpponentsTurn()) {
            const teamUserIds = onlinePlayOnlineGameService.currentOnlineGame()?.team_user_ids ?? [];
            const isInvitedOnlineTeamPlayer =
                teamUserIds.length === 2 &&
                teamUserIds.every((team) => team.user_ids.length === 2) &&
                teamUserIds.some((team) => team.user_ids[1] === authService.user.id);

            alertService.createAlert({
                title: $localize`:@@CANT_LEAVE_RIGHT_NOW:You can't leave right now`,
                text: isInvitedOnlineTeamPlayer
                    ? $localize`:@@ONLY_TEAM_HOST_CAN_LEAVE_MATCH:Only the team host can leave the match`
                    : $localize`:@@ONLY_LEAVE_IN_YOUR_TURN:You can only leave the match when it's your turn`,
                icon: 'warning',
            });
            return false;
        }

        return leaveBusterService.startOnlineLeaveProcess().pipe(
            tap((hasLeft) => {
                if (hasLeft) {
                    if (onlineGameplayTypeAlwaysShowEndScreen(leaveBusterService.currentGameplayType())) {
                        leaveBusterService.openOnlineEndScreen$.next();
                    } else {
                        if (!leaveBusterService.gameIsFinished) {
                            afterGameService.cleanupAfterOnlineGame(true);
                        }
                    }
                }
            })
        );
    }
};
